<template>
  <button 
    :class="buttonClass" 
    @click="handleClick"
    :disabled="disabled"
    :aria-label="ariaLabel"
  >
    <slot></slot>
    <span v-if="loading" class="loading-spinner" aria-hidden="true">⏳</span>
  </button>
</template>

<script>
export default {
  name: 'ActionButton',
  props: {
    buttonClass: {
      type: String,
      default: 'action-btn'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      required: true
    }
  },
  emits: ['click'],
  methods: {
    handleClick() {
      if (!this.disabled) {
        this.$emit('click');
      }
    }
  }
}
</script>

<style scoped>
.action-btn {
  /* Apex主题按钮 - 渐变紫色 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
}

.action-btn:hover:not(:disabled) {
  /* 悬停效果 */
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 补录按钮 */
.backfill-btn {
  background: linear-gradient(135deg, #ffcc00 0%, #ff9900 100%); /* 金色渐变 */
  color: #0d1a2d; /* 深蓝文字 */
  border: none;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
}

.backfill-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff9900 0%, #ffcc00 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.6);
}

/* 删除按钮 */
.delete-btn {
  background: rgba(255, 69, 69, 0.2);
  border: 1px solid rgba(255, 69, 69, 0.5);
  color: #ff4545;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.delete-btn:hover:not(:disabled) {
  background: rgba(255, 69, 69, 0.3);
  transform: translateY(-1px);
}

/* 确认删除按钮 */
.confirm-delete-btn {
  background: linear-gradient(135deg, #ff4545 0%, #cc0000 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 69, 69, 0.4);
  font-weight: bold;
}

.confirm-delete-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #cc0000 0%, #ff4545 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 69, 69, 0.6);
}

/* 取消按钮 */
.cancel-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.cancel-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
}

/* 提交按钮 */
.submit-btn {
  /* Apex主题按钮 - 渐变紫色 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  align-self: center;
  min-width: 120px;
  margin-top: 10px;
}

.submit-btn:hover:not(:disabled) {
  /* 悬停效果 */
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

/* 生成ID按钮 */
.generate-btn {
  background: rgba(255, 204, 0, 0.2);
  border: 1px solid rgba(255, 204, 0, 0.5);
  color: #ffcc00;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.generate-btn:hover:not(:disabled) {
  background: rgba(255, 204, 0, 0.3);
  transform: translateY(-2px);
}

/* 复制按钮 */
.copy-btn {
  background: rgba(102, 126, 234, 0.2);
  border: 1px solid rgba(102, 126, 234, 0.5);
  color: #667eea;
  padding: 4px 10px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.copy-btn:hover:not(:disabled) {
  background: rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

/* 关闭按钮 */
.close-btn {
  background: none;
  border: none;
  color: #ffcc00;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover:not(:disabled) {
  background: rgba(255, 204, 0, 0.2);
  transform: rotate(90deg);
}

.loading-spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
  margin-left: 5px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .action-btn {
    padding: 10px 15px;
    font-size: 0.9rem;
    min-width: 70px;
    max-width: 100px;
  }
  
  .submit-btn {
    padding: 10px 15px;
    font-size: 0.9rem;
    min-width: 100px;
  }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
  .action-btn {
    padding: 8px 12px;
    font-size: 0.85rem;
    min-width: 60px;
    max-width: 90px;
  }
  
  .submit-btn {
    padding: 10px 15px;
    font-size: 0.9rem;
    min-width: 100px;
  }
}
</style>